const createSettingDefinition = (definition) => {
  return Object.freeze({
    writable: true,
    publicApi: Object.freeze({
      readable: definition?.persistence === 'synced',
      writable: definition?.persistence === 'synced'
    }),
    ...definition
  });
};

export const GENERAL_OPEN_DESTINATION_VALUES = Object.freeze(['cursor', 'vscode', 'vscode_insiders', 'windsurf', 'zed', 'xcode', 'nova', 'sublime_text', 'textmate', 'bbedit', 'intellij_idea', 'intellij_idea_ce', 'webstorm', 'pycharm', 'clion', 'goland', 'rubymine', 'phpstorm', 'rider', 'fleet', 'vim', 'neovim', 'notepad_plus_plus', 'finder', 'system']);

export const GENERAL_THREAD_DETAIL_VALUES = Object.freeze(['steps', 'steps_with_code_commands', 'full_command_output']);
export const PERSONALIZATION_PERSONALITY_VALUES = Object.freeze(['friendly', 'pragmatic']);
export const SIDEBAR_THREAD_SORT_BY_VALUES = Object.freeze(['created', 'updated']);
export const SIDEBAR_THREAD_SHOW_MODE_VALUES = Object.freeze(['all', 'relevant']);
export const PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_OPTIONS = Object.freeze([
  { value: 'auto', label: 'Auto-detect', description: 'Use your system language', flag: '🌐' },
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'es', label: 'Spanish', flag: '🇪🇸' },
  { value: 'fr', label: 'French', flag: '🇫🇷' },
  { value: 'de', label: 'German', flag: '🇩🇪' },
  { value: 'pt-br', label: 'Portuguese (Brazil)', flag: '🇧🇷' },
  { value: 'it', label: 'Italian', flag: '🇮🇹' },
  { value: 'nl', label: 'Dutch', flag: '🇳🇱' },
  { value: 'ja', label: 'Japanese', flag: '🇯🇵' },
  { value: 'ko', label: 'Korean', flag: '🇰🇷' },
  { value: 'zh-cn', label: 'Chinese (Simplified)', flag: '🇨🇳' },
  { value: 'zh-tw', label: 'Chinese (Traditional)', flag: '🇹🇼' },
  { value: 'hi', label: 'Hindi', flag: '🇮🇳' },
  { value: 'ar', label: 'Arabic', flag: '🇸🇦' },
  { value: 'ru', label: 'Russian', flag: '🇷🇺' }
]);
export const PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_VALUES = Object.freeze(
  PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_OPTIONS.map((option) => {
    return option.value;
  })
);
export const THEME_VALUES = Object.freeze(['light', 'dark', 'auto', 'system']);

export const DEFAULT_PIEVE_SANS_FONT_SIZE_PX = 13;
export const DEFAULT_GENERAL_OPEN_DESTINATION_OPTIONS = Object.freeze([{ value: 'system', label: 'System default' }]);
export const GENERAL_THREAD_DETAIL_OPTIONS = Object.freeze([
  { value: 'steps', label: 'Steps', description: 'Hide commands and outputs' },
  { value: 'steps_with_code_commands', label: 'Steps with code commands', description: 'Show commands, collapse output' },
  { value: 'full_command_output', label: 'Steps with code output', description: 'Show commands and expand output' }
]);

export const pieveSettingsDefinitions = Object.freeze([
  createSettingDefinition({
    key: 'defaultOpenDestination',
    label: 'Default open destination',
    description: 'Where files and folders open by default.',
    intentAliases: ['default open destination', 'open destination', 'default editor', 'open files with', 'open in cursor', 'open in vscode', 'open in vs code'],
    sectionId: 'general',
    control: 'select',
    persistence: 'local',
    type: 'enum',
    enumValues: GENERAL_OPEN_DESTINATION_VALUES,
    defaultValue: 'cursor',
    legacyGroupKey: 'general'
  }),
  createSettingDefinition({
    key: 'threadDetail',
    label: 'Task detail',
    description: 'Choose how much command output to show in tasks.',
    intentAliases: ['task detail', 'thread detail', 'command output', 'full command output'],
    sectionId: 'general',
    control: 'select',
    persistence: 'synced',
    type: 'enum',
    enumValues: GENERAL_THREAD_DETAIL_VALUES,
    defaultValue: 'steps_with_code_commands',
    legacyGroupKey: 'general'
  }),
  createSettingDefinition({
    key: 'preventSleepWhileRunning',
    label: 'Prevent sleep while running',
    description: 'Keep the machine awake while Pieve is working.',
    intentAliases: ['prevent sleep', 'keep awake', 'sleep while running'],
    sectionId: 'general',
    control: 'toggle',
    persistence: 'local',
    type: 'boolean',
    defaultValue: true,
    legacyGroupKey: 'general'
  }),
  createSettingDefinition({
    key: 'playThreadFinishSound',
    label: 'Play task completion sound',
    description: 'Play a sound when a task finishes in another thread.',
    sectionId: 'general',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'general'
  }),
  createSettingDefinition({
    key: 'emailOrganizationConnectionChanges',
    label: 'Organization connection change emails',
    description: 'Email me when an organization connection I use is changed.',
    intentAliases: ['connection change emails', 'organization connection notifications', 'shared connection emails'],
    sectionId: 'personalization',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: true,
    legacyGroupKey: 'account'
  }),
  createSettingDefinition({
    key: 'theme',
    label: 'Theme',
    description: 'Choose the visual theme for Pieve.',
    intentAliases: ['theme', 'dark mode', 'light mode', 'system theme', 'auto theme'],
    sectionId: 'general',
    control: 'select',
    persistence: 'synced',
    type: 'enum',
    enumValues: THEME_VALUES,
    defaultValue: 'system',
    legacyGroupKey: 'appearance'
  }),
  createSettingDefinition({
    key: 'useOpaqueWindowBackground',
    label: 'Use opaque window background',
    description: 'Render windows with an opaque background.',
    sectionId: 'general',
    control: 'toggle',
    persistence: 'local',
    writable: false,
    type: 'boolean',
    defaultValue: true,
    legacyGroupKey: 'appearance'
  }),
  createSettingDefinition({
    key: 'usePointerCursors',
    label: 'Use pointer cursors',
    description: 'Use pointer cursors for interactive UI elements.',
    sectionId: 'general',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'appearance'
  }),
  createSettingDefinition({
    key: 'sansFontFamily',
    label: 'Sans font family',
    description: 'Primary interface font family.',
    intentAliases: ['sans font', 'sans font family', 'ui font', 'interface font', 'font family', 'pieve font'],
    sectionId: 'general',
    control: 'font-family',
    persistence: 'local',
    type: 'fontFamily',
    maxLength: 240,
    defaultValue: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    legacyGroupKey: 'appearance'
  }),
  createSettingDefinition({
    key: 'sansFontSizePx',
    label: 'Sans font size',
    description: 'Primary interface font size in pixels.',
    sectionId: 'general',
    control: 'font-size',
    persistence: 'synced',
    type: 'integer',
    min: 10,
    max: 24,
    defaultValue: DEFAULT_PIEVE_SANS_FONT_SIZE_PX,
    legacyGroupKey: 'appearance'
  }),
  createSettingDefinition({
    key: 'codeFontFamily',
    label: 'Code font family',
    description: 'Monospace font family used for code and terminal output.',
    intentAliases: ['code font', 'code font family', 'terminal font', 'monospace font'],
    sectionId: 'general',
    control: 'font-family',
    persistence: 'local',
    type: 'fontFamily',
    maxLength: 240,
    defaultValue: 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace',
    legacyGroupKey: 'appearance'
  }),
  createSettingDefinition({
    key: 'codeFontSizePx',
    label: 'Code font size',
    description: 'Code and terminal font size in pixels.',
    sectionId: 'general',
    control: 'font-size',
    persistence: 'synced',
    type: 'integer',
    min: 10,
    max: 24,
    defaultValue: 12,
    legacyGroupKey: 'appearance'
  }),
  createSettingDefinition({
    key: 'personality',
    label: 'Personality',
    description: 'Choose a default tone for Pieve responses.',
    intentAliases: ['personality', 'tone'],
    sectionId: 'personalization',
    control: 'select',
    persistence: 'synced',
    type: 'enum',
    enumValues: PERSONALIZATION_PERSONALITY_VALUES,
    defaultValue: 'friendly',
    legacyGroupKey: 'personalization'
  }),
  createSettingDefinition({
    key: 'preferredResponseLanguage',
    label: 'Preferred response language',
    description: 'Choose the language Pieve should use by default when responding.',
    sectionId: 'personalization',
    control: 'select',
    persistence: 'synced',
    type: 'enum',
    enumValues: PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_VALUES,
    defaultValue: 'auto',
    legacyGroupKey: 'personalization'
  }),
  createSettingDefinition({
    key: 'customInstructions',
    label: 'Custom instructions',
    description: 'Instructions that tailor Pieve to you.',
    sectionId: 'personalization',
    control: 'textarea',
    persistence: 'synced',
    type: 'text',
    maxLength: 8000,
    defaultValue: '',
    legacyGroupKey: 'personalization'
  }),
  createSettingDefinition({
    key: 'threadNamingPreferences',
    label: 'Task naming preferences',
    description: 'Instructions for how AI should help name your tasks.',
    sectionId: 'personalization',
    control: 'textarea',
    persistence: 'synced',
    type: 'text',
    maxLength: 8000,
    defaultValue: '',
    legacyGroupKey: 'personalization'
  }),
  createSettingDefinition({
    key: 'enableCodeMode',
    label: 'Enable code mode',
    description: 'Turn on Pieve for developer workflows.',
    sectionId: 'features',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'features'
  }),
  createSettingDefinition({
    key: 'allowProjectFolders',
    label: 'Allow project folders',
    description: 'Show non-Home project folders and project controls in the sidebar.',
    sectionId: 'features',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: true,
    legacyGroupKey: 'features'
  }),
  createSettingDefinition({
    key: 'threadSortBy',
    label: 'Task sort order',
    description: 'Choose whether sidebar tasks sort by created time or updated time.',
    sectionId: 'features',
    control: 'select',
    persistence: 'synced',
    type: 'enum',
    enumValues: SIDEBAR_THREAD_SORT_BY_VALUES,
    defaultValue: 'updated',
    legacyGroupKey: 'features'
  }),
  createSettingDefinition({
    key: 'threadShowMode',
    label: 'Task visibility',
    description: 'Choose whether the sidebar shows all tasks or only relevant tasks.',
    sectionId: 'features',
    control: 'select',
    persistence: 'synced',
    type: 'enum',
    enumValues: SIDEBAR_THREAD_SHOW_MODE_VALUES,
    defaultValue: 'all',
    legacyGroupKey: 'features'
  }),
  createSettingDefinition({
    key: 'enableTerminal',
    label: 'Enable terminal',
    description: 'Show terminal-specific controls inside code mode.',
    sectionId: 'features',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'features'
  }),
  createSettingDefinition({
    key: 'allowAgentToUsePython',
    label: 'Allow an agent to use Python',
    description: 'Allow Pieve to use Python-based coding workflows.',
    sectionId: 'features',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'features'
  }),
  createSettingDefinition({
    key: 'allowAgentToUseGit',
    label: 'Allow an agent to use Git',
    description: 'Allow Pieve to use Git-driven coding workflows.',
    sectionId: 'features',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'features'
  }),
  createSettingDefinition({
    key: 'enableRemoteControl',
    label: 'Enable remote control',
    description: 'Let Pieve control your desktop from web tasks when you ask.',
    intentAliases: ['remote control', 'control my desktop', 'control this desktop'],
    sectionId: 'superpowers',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'superpowers'
  }),
  createSettingDefinition({
    key: 'defaultRemoteDeviceId',
    label: 'Default device',
    description: 'Which desktop device Pieve should control from web tasks.',
    sectionId: 'superpowers',
    control: 'select',
    persistence: 'synced',
    type: 'text',
    maxLength: 64,
    defaultValue: '',
    legacyGroupKey: 'superpowers'
  }),
  createSettingDefinition({
    key: 'allowLearnAboutYourJob',
    label: 'Allow Pieve to learn about your job',
    description: 'Let Pieve remember your role, goals, and workflows.',
    sectionId: 'superpowers',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'superpowers'
  }),
  createSettingDefinition({
    key: 'allowLearnAboutYourApps',
    label: 'Allow Pieve to learn about your apps',
    description: 'Let Pieve learn how you use connected apps.',
    sectionId: 'superpowers',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'superpowers'
  }),
  createSettingDefinition({
    key: 'branchPrefix',
    label: 'Branch prefix',
    description: 'Prefix used when creating new branches in Pieve.',
    sectionId: 'git',
    control: 'input',
    persistence: 'synced',
    type: 'branchPrefix',
    maxLength: 120,
    defaultValue: 'pieve/',
    legacyGroupKey: 'git'
  }),
  createSettingDefinition({
    key: 'alwaysForcePush',
    label: 'Always force push',
    description: 'Use force-with-lease when pushing from Pieve.',
    sectionId: 'git',
    control: 'toggle',
    persistence: 'synced',
    type: 'boolean',
    defaultValue: false,
    legacyGroupKey: 'git'
  }),
  createSettingDefinition({
    key: 'commitInstructions',
    label: 'Commit instructions',
    description: 'Guidance appended to commit message generation prompts.',
    sectionId: 'git',
    control: 'textarea',
    persistence: 'synced',
    type: 'text',
    maxLength: 8000,
    defaultValue: '',
    legacyGroupKey: 'git'
  }),
  createSettingDefinition({
    key: 'pullRequestInstructions',
    label: 'Pull request instructions',
    description: 'Guidance appended to pull request bodies created by Pieve.',
    sectionId: 'git',
    control: 'textarea',
    persistence: 'synced',
    type: 'text',
    maxLength: 8000,
    defaultValue: '',
    legacyGroupKey: 'git'
  })
]);

export const PIEVE_SETTING_DEFINITION_BY_KEY = Object.freeze(
  Object.fromEntries(
    pieveSettingsDefinitions.map((definition) => {
      return [definition.key, definition];
    })
  )
);
